import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import BgGlassmorphism from "components/BgGlassmorphism/BgGlassmorphism";
import SectionMagazine5 from "./SectionMagazine5";
import SectionLatestPosts from "./SectionLatestPosts";
import { fetchBlogArticles } from "../../data/blogArticle/_requests"; // import the fetchBlogArticles function
import Pagination from "shared/Pagination/Pagination";
import Input from "shared/Input/Input";
import Heading1 from "components/Heading/Heading1";

const POSTS_PER_PAGE = 10;
const BLOG_DESCRIPTION =
  "Explore top places to visit, adventures, and beauty of Sri Lanka and Australia in our comprehensive travel guide. Unveil the wonders today!";

// Page 1 lives at /blog; later pages get their own crawlable URL
const blogPageHref = (page: number) =>
  page === 1 ? "/blog" : `/blog/page/${page}`;

const BlogPage = () => {
  const { page } = useParams<{ page: string }>();
  const navigate = useNavigate();
  const urlPage = page === undefined ? 1 : Number(page);
  const isValidUrlPage =
    page === undefined || (Number.isInteger(urlPage) && urlPage >= 2);

  const [posts, setPosts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalItems, setTotalItems] = useState(0);
  const [searchPage, setSearchPage] = useState(1);

  // Search results are paged in local state, not in the URL
  const isSearching = searchTerm.length >= 4;
  const currentPage = isSearching ? searchPage : urlPage;

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setSearchPage(1);
  };

  useEffect(() => {
    console.log("searchTerm :: ", searchTerm);
    const fetchPosts = async () => {
      try {
        const params = {
          page: currentPage,
          limit: POSTS_PER_PAGE,
          search: isSearching ? searchTerm : null,
        };
        const response = await fetchBlogArticles(params); // Fetch 15 posts

        // Past the last page: send crawlers and visitors back to page 1
        if (!isSearching && currentPage > 1 && response.data.data.length === 0) {
          navigate("/blog", { replace: true });
          return;
        }

        setPosts(response.data.data);
        setTotalItems(response.data.pagination.total);
      } catch (error) {
        console.error("Error fetching posts:", error);
      }
    };

    if (isValidUrlPage && (isSearching || searchTerm.length === 0)) {
      fetchPosts();
    }
  }, [currentPage, searchTerm, isSearching, isValidUrlPage, navigate]);

  if (!isValidUrlPage) {
    return <Navigate to="/blog" replace />;
  }

  const magazinePosts = posts.slice(0, 4); // Get the first 4 posts for the magazine section
  const latestPosts = posts.slice(4); // Get the rest of the posts for the latest posts section
  const pageSuffix = urlPage > 1 ? ` – Page ${urlPage}` : "";

  return (
    <div className="nc-BlogPage overflow-hidden relative">
      <Helmet>
        <title>{`Blog${pageSuffix} | My Flight Zone`}</title>

        <meta
          name="description"
          content={
            urlPage > 1
              ? `${BLOG_DESCRIPTION} Page ${urlPage}.`
              : BLOG_DESCRIPTION
          }
        />

        <link
          rel="canonical"
          href={`https://myflightzone.com.au${blogPageHref(urlPage)}`}
        />
      </Helmet>

      <BgGlassmorphism />
      <div className="container relative mt-5">
        <Heading1
          heading="My Flight Zone - Blog"
          subHeading="Your Ultimate Guide to Unforgettable Journeys"
        />
        <Input
          type="search"
          placeholder="Search blogs"
          onChange={handleSearch}
          value={searchTerm}
        />
      </div>
      <div className="container relative">
        {/* === SECTION 1 === */}
        <div className="pt-12 pb-16 lg:pb-28">
          <SectionMagazine5 posts={magazinePosts} />
        </div>

        {/* === SECTION 8 === */}
        <SectionLatestPosts posts={latestPosts} />

        <Pagination
          totalItems={totalItems}
          itemsPerPage={POSTS_PER_PAGE}
          currentPage={currentPage}
          onChangePage={setSearchPage}
          getPageHref={isSearching ? undefined : blogPageHref}
        />
        <br />
        <br />
      </div>
    </div>
  );
};

export default BlogPage;
